import React, { useState, useEffect } from 'react';
import api, { getWebhookInfo, getWebhookLogs } from '../services/api';
import { Copy, RefreshCw, CheckCircle, XCircle, Clock, Loader2, Eye, X, ChevronLeft, ChevronRight, ExternalLink, Settings, Zap, CreditCard, Smartphone, FileText, AlertTriangle, TrendingUp, Send } from 'lucide-react';
import { IntegrationsTab } from './Settings';
import MetaLogs from './MetaLogs';

const PLATFORMS = [
  { id: 'braip', name: 'Braip', color: '#00C853', logoUrl: 'https://www.google.com/s2/favicons?domain=braip.com&sz=64', instructions: ['Acesse o painel da Braip','Va em Configuracoes > Integrações > Webhook','Clique em Adicionar Webhook','Cole a URL acima','Selecione os eventos desejados','Salve'], docUrl: 'https://docs.eadplataforma.com/docs/webhooks' },
  { id: 'payt', name: 'Payt', color: '#6366F1', logoUrl: 'https://www.google.com/s2/favicons?domain=payt.com.br&sz=64', instructions: ['Acesse o painel da Payt','Va em Configuracoes > Webhooks','Clique em Novo Webhook','Cole a URL acima','Selecione os eventos desejados','Salve'], docUrl: 'https://help.payt.com.br/article/155-postback' },
  { id: 'hotmart', name: 'Hotmart', color: '#FF6B00', logoUrl: 'https://www.google.com/s2/favicons?domain=hotmart.com&sz=64', instructions: ['Acesse o painel da Hotmart','Va em Ferramentas > Webhooks','Configure o Webhook','Cole a URL acima','Marque os eventos desejados','Copie o Hottok e configure no JTracker','Salve'], docUrl: 'https://developers.hotmart.com/docs/pt-BR/1.0.0/webhook/using-webhook/', hasToken: true, tokenName: 'Hottok' },
  { id: 'monetizze', name: 'Monetizze', color: '#00BFA5', logoUrl: 'https://monetizze.com.br/wp-content/uploads/2021/03/favicon.png', instructions: ['Acesse o painel da Monetizze','Va em Configuracoes > Postback','Adicione a URL acima','Selecione os eventos','Salve'], docUrl: 'https://api.monetizze.com.br/2.1/apidoc/#api-Postback' },
  { id: 'perfectpay', name: 'PerfectPay', color: '#8B5CF6', logoUrl: 'https://www.google.com/s2/favicons?domain=perfectpay.com.br&sz=64', instructions: ['Acesse o painel da PerfectPay','Va em Configuracoes > Webhooks','Clique em Novo Webhook','Cole a URL acima','Selecione os eventos','Salve'], docUrl: 'https://docs.eadplataforma.com/docs/webhooks' },
  { id: '123logs', name: '123logs (Logística)', color: '#F59E0B', logoUrl: 'https://www.google.com/s2/favicons?domain=123logs.com.br&sz=64', instructions: ['Acesse o painel da 123logs','Localize a área de Integrações/Webhooks','Adicione a URL acima como destino','Selecione os eventos de postagem e entrega','Salve'], docUrl: 'https://123logs.com.br' },
  { id: 'kiwify', name: 'Kiwify', color: '#10B981', logoUrl: 'https://www.google.com/s2/favicons?domain=kiwify.com.br&sz=64', instructions: ['Acesse o painel da Kiwify','Va em Configuracoes > Webhooks','Adicione Webhook','Cole a URL acima','Selecione os eventos','Salve'], docUrl: 'https://docs.kiwify.com.br/api-reference/webhooks/create' },
  { id: 'cakto', name: 'Cakto', color: '#F59E0B', instructions: ['Acesse o painel da Cakto','Va em Integrações > Webhooks','Crie Webhook','Cole a URL acima','Selecione os eventos','Salve'], docUrl: 'https://ajuda.cakto.com.br/pt/article/como-funcionam-os-webhooks-1l9m78k/' },
  { id: 'loggz', name: 'Loggz', color: '#3B82F6', instructions: ['Acesse o painel da Loggz','Va em Configuracoes > Integrações','Clique em Webhooks','Adicione a URL acima','Selecione os eventos','Salve'], docUrl: 'https://blog.logzz.com.br/webhooks/' }
];

const EVENT_TYPES = [
  { id: 'purchase', name: 'Venda Aprovada', icon: CheckCircle, color: 'green' },
  { id: 'refund', name: 'Reembolso', icon: XCircle, color: 'red' },
  { id: 'chargeback', name: 'Chargeback', icon: AlertTriangle, color: 'red' },
  { id: 'abandoned', name: 'Carrinho Abandonado', icon: Clock, color: 'yellow' },
  { id: 'pix_generated', name: 'Pix Gerado', icon: Smartphone, color: 'cyan' },
  { id: 'boleto_generated', name: 'Boleto Gerado', icon: FileText, color: 'blue' },
  { id: 'boleto_expired', name: 'Boleto Vencido', icon: FileText, color: 'orange' },
  { id: 'card_refused', name: 'Cartao Recusado', icon: CreditCard, color: 'gray' },
];

const Webhooks = () => {
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchData = async (currentPage = 1) => {
    setLoading(true);
    try {
      const [infoRes, logsRes] = await Promise.all([getWebhookInfo(), getWebhookLogs({ limit: 10, page: currentPage })]);
      setWebhookInfo(infoRes.data);
      setLogs(logsRes.data.logs || []);
      setPage(logsRes.data.page || 1);
      setTotalPages(logsRes.data.totalPages || 1);
      setTotal(logsRes.data.total || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const copyToClipboard = (text, key) => { navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(''), 2000); };

  const getWebhookUrl = (platformId) => {
    if (!webhookInfo) return '';
    const baseUrl = webhookInfo.baseUrl || 'https://api.jtracking.app';
    return baseUrl + '/api/webhook/' + webhookInfo.userId + '?platform=' + platformId;
  };

  const testWebhook = async (platformId) => {
    setTesting(true); setTestResult(null);
    try {
      const res = await api.post('/webhook/test-connection', { platform: platformId });
      setTestResult({ success: res.data.success });
    } catch (e) {
      setTestResult({ success: false, error: e.response?.data?.error || 'Erro' });
    }
    setTesting(false);
  };

  const getStatusIcon = (status) => {
    if (status === 'success') return <CheckCircle size={18} className="text-green-400" />;
    if (status === 'error') return <XCircle size={18} className="text-red-400" />;
    return <Clock size={18} className="text-yellow-400" />;
  };

  const getEventIcon = (eventType) => {
    const event = EVENT_TYPES.find(e => e.id === eventType);
    if (!event) return <Zap size={16} />;
    const Icon = event.icon;
    const colorMap = { green: 'text-green-400', red: 'text-red-400', yellow: 'text-yellow-400', blue: 'text-blue-400', cyan: 'text-cyan-400', gray: 'text-gray-400', orange: 'text-orange-400' };
    return <Icon size={16} className={colorMap[event.color] || 'text-gray-400'} />;
  };

  const getEventBadgeColor = (eventType) => {
    const event = EVENT_TYPES.find(e => e.id === eventType);
    if (!event) return 'bg-gray-700 text-gray-300';
    const colorMap = { green: 'bg-green-500/20 text-green-400', red: 'bg-red-500/20 text-red-400', yellow: 'bg-yellow-500/20 text-yellow-400', blue: 'bg-blue-500/20 text-blue-400', cyan: 'bg-cyan-500/20 text-cyan-400', gray: 'bg-gray-500/20 text-gray-400', orange: 'bg-orange-500/20 text-orange-400' };
    return colorMap[event.color] || 'bg-gray-700 text-gray-300';
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('pt-BR') + ' ' + new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  if (loading && logs.length === 0) return <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-blue-500" size={40} /></div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Zap className="text-yellow-500" />Webhooks</h1>
          <p className="text-gray-400">Conecte suas plataformas de pagamento</p>
        </div>
        <button onClick={() => fetchData(page)} className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white"><RefreshCw size={20} className={loading ? 'animate-spin' : ''} /></button>
      </div>

      {webhookInfo && (
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <p className="text-gray-400 text-sm mb-2">URL Base do Webhook</p>
          <code className="block bg-gray-900 rounded-lg px-4 py-2 text-green-400 text-sm font-mono truncate">{webhookInfo.baseUrl || 'https://api.jtracking.app'}/api/webhook/[userId]?platform=[plataforma]</code>
        </div>
      )}

      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Settings size={20} className="text-blue-400" />Plataformas</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {PLATFORMS.map(p => (
            <button key={p.id} onClick={() => setSelectedPlatform(p)} className="flex items-center gap-3 p-4 bg-gray-900/50 rounded-xl border border-gray-700 hover:border-blue-500/50 transition-all">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: p.color }}>
                {p.logoUrl ? <img src={p.logoUrl} alt={p.name} className="w-6 h-6 object-contain" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                : null}
                <span className="text-white font-bold text-sm" style={{ display: p.logoUrl ? 'none' : 'flex' }}>{p.name.charAt(0)}</span>
              </div>
              <div className="text-left"><p className="text-white font-medium text-sm">{p.name}</p><p className="text-gray-500 text-xs">Configurar</p></div>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-700"><h2 className="text-lg font-bold text-white">Histórico ({total})</h2></div>
        {logs.length === 0 ? (
          <div className="text-center py-12 text-gray-500"><Zap size={40} className="mx-auto mb-3 opacity-50" /><p>Nenhum webhook recebido ainda</p></div>
        ) : (
          <>
            <div className="divide-y divide-gray-700">
              {logs.map(log => (
                <div key={log.id} className="p-4 hover:bg-gray-700/30 flex items-center gap-4 cursor-pointer" onClick={() => setSelectedLog(log)}>
                  {getStatusIcon(log.status)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getEventIcon(log.eventType)}
                      <span className={`px-2 py-0.5 rounded text-xs ${getEventBadgeColor(log.eventType)}`}>{EVENT_TYPES.find(e => e.id === log.eventType)?.name || log.eventType}</span>
                      <span className="text-gray-500 text-xs">{log.platform}</span>
                    </div>
                    <p className="text-gray-500 text-xs">{formatDate(log.createdAt)}{log.processingTime ? ` - ${log.processingTime}ms` : ''}</p>
                  </div>
                  <Eye size={18} className="text-gray-500" />
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="p-4 border-t border-gray-700 flex items-center justify-between">
                <button onClick={() => fetchData(page - 1)} disabled={page <= 1} className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 text-white rounded-lg disabled:opacity-50"><ChevronLeft size={16} />Anterior</button>
                <span className="text-gray-400 text-sm">Página {page} de {totalPages}</span>
                <button onClick={() => fetchData(page + 1)} disabled={page >= totalPages} className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 text-white rounded-lg disabled:opacity-50">Próxima<ChevronRight size={16} /></button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedPlatform && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: selectedPlatform.color }}>
                  {selectedPlatform.logoUrl ? <img src={selectedPlatform.logoUrl} alt={selectedPlatform.name} className="w-6 h-6 object-contain" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} /> : null}
                  <span className="text-white font-bold" style={{ display: selectedPlatform.logoUrl ? 'none' : 'flex' }}>{selectedPlatform.name.charAt(0)}</span>
                </div>
                <h3 className="text-lg font-bold text-white">{selectedPlatform.name}</h3>
              </div>
              <button onClick={() => setSelectedPlatform(null)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-gray-400 text-sm mb-2 block">URL do Webhook</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-900 rounded-lg px-4 py-2.5 text-green-400 text-sm font-mono truncate">{getWebhookUrl(selectedPlatform.id)}</code>
                  <button onClick={() => copyToClipboard(getWebhookUrl(selectedPlatform.id), selectedPlatform.id)} className={`p-2.5 rounded-lg ${copied === selectedPlatform.id ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'}`}>{copied === selectedPlatform.id ? <CheckCircle size={18} /> : <Copy size={18} />}</button>
                </div>
              </div>
              <div>
                <h4 className="text-white font-medium mb-2">Passo a passo</h4>
                <ol className="space-y-2">
                  {selectedPlatform.instructions.map((step, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-300 text-sm"><span className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0 mt-0.5">{i + 1}</span>{step}</li>
                  ))}
                </ol>
              </div>
              <div className="flex gap-2">
                <button onClick={() => testWebhook(selectedPlatform.id)} disabled={testing} className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50 flex items-center justify-center gap-2">{testing ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}{testing ? 'Testando...' : 'Testar Conexão'}</button>
                {selectedPlatform.docUrl && <a href={selectedPlatform.docUrl} target="_blank" rel="noopener noreferrer" className="px-4 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600 flex items-center gap-2"><ExternalLink size={16} />Docs</a>}
              </div>
              {testResult && (
                <div className={`p-3 rounded-lg ${testResult.success ? 'bg-green-900/30 border border-green-700 text-green-400' : 'bg-red-900/30 border border-red-700 text-red-400'}`}>
                  {testResult.success ? <><CheckCircle size={16} className="inline mr-2" />Conexão OK!</> : <><XCircle size={16} className="inline mr-2" />{testResult.error || 'Erro'}</>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedLog && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Detalhes do Webhook</h3>
              <button onClick={() => setSelectedLog(null)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-gray-500 text-xs">Plataforma</p><p className="text-white">{selectedLog.platform}</p></div>
                <div><p className="text-gray-500 text-xs">Evento</p><p className="text-white">{EVENT_TYPES.find(e => e.id === selectedLog.eventType)?.name || selectedLog.eventType}</p></div>
                <div><p className="text-gray-500 text-xs">Status</p><p className={selectedLog.status === 'success' ? 'text-green-400' : 'text-red-400'}>{selectedLog.status}</p></div>
                <div><p className="text-gray-500 text-xs">Data</p><p className="text-white">{formatDate(selectedLog.createdAt)}</p></div>
                {selectedLog.paymentMethod && <div><p className="text-gray-500 text-xs">Pagamento</p><p className="text-white">{selectedLog.paymentMethod}</p></div>}
                {selectedLog.processingTime && <div><p className="text-gray-500 text-xs">Tempo</p><p className="text-white">{selectedLog.processingTime}ms</p></div>}
              </div>
              {selectedLog.errorMessage && <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">{selectedLog.errorMessage}</div>}
              <div>
                <p className="text-gray-500 text-xs mb-2">Payload</p>
                <pre className="bg-gray-900 rounded-lg p-4 text-green-400 text-xs overflow-auto max-h-60 font-mono">{JSON.stringify(typeof selectedLog.payload === 'string' ? JSON.parse(selectedLog.payload) : selectedLog.payload, null, 2)}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// INTEGRACOES (revisao 15/07): wrapper com abas Webhooks / Meta & CRM
const Integracoes = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const [tab, setTab] = React.useState(['meta', 'logs'].includes(urlParams.get('tab')) ? urlParams.get('tab') : 'webhooks');
  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-white">Integrações</h1>
        <p className="text-gray-400 text-sm">Webhooks, Meta e CRM em um só lugar</p>
      </div>
      <div className="flex items-center gap-2 bg-gray-800 rounded-lg p-1 w-fit">
        <button onClick={() => setTab('webhooks')} className={`px-4 py-2 rounded-lg text-sm ${tab === 'webhooks' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>Webhooks</button>
        <button onClick={() => setTab('meta')} className={`px-4 py-2 rounded-lg text-sm ${tab === 'meta' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>Meta & CRM</button>
        <button onClick={() => setTab('logs')} className={`px-4 py-2 rounded-lg text-sm ${tab === 'logs' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>Logs de Envio</button>
      </div>
      {tab === 'webhooks' ? <Webhooks /> : tab === 'logs' ? <MetaLogs /> : <IntegrationsTab />}
    </div>
  );
};

export default Integracoes;
